/* eslint-disable no-param-reassign */
import { DirectiveOptions, VNode } from 'vue';

/**
 * v-track-click Directive
 *
 * Tracks click events by pushing data to the GTM dataLayer.
 *
 * Usage:
 *   v-track-click="'Page_Section__Element__Action'"
 *   v-track-click="'Checkout__PlaceOrder'"
 *   v-track-click="'PDP__CTA__Shop_Now'"
 *
 * Note: Use semantic page/section names, NOT component names (e.g., 'Checkout' not '$options.name')
 *
 * @see TRACKING_STANDARDS.md for naming conventions and best practices
 */

// Extend Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer?: any[];
  }

  interface HTMLElement {
    _trackClickExpression?: string;
    _trackClickValue?: any;
    _trackClickInstance?: any;
  }
}

export const TrackClick: DirectiveOptions = {
  bind(el: HTMLElement, binding: any, vnode: VNode) {
    // Store the binding expression and component context on the element
    el._trackClickExpression = binding.expression;
    el._trackClickValue = binding.value;
    el._trackClickInstance = vnode.context;

    el.addEventListener('click', () => {
      // Get the latest value of the expression
      let trackValue = el._trackClickValue;

      // Handle different value types
      if (trackValue === undefined || trackValue === null) {
        console.warn('TrackClick directive received undefined or null value');
        return; // Skip tracking if value is undefined or null
      }

      // If the value is a function call expression (ends with "()" or similar)
      if (typeof el._trackClickExpression === 'string'
          && el._trackClickExpression.match(/\([^)]*\)$/)
          && el._trackClickInstance) {
        try {
          // Get the function name without the parentheses
          const fnName = el._trackClickExpression.replace(/\([^)]*\)$/, '');

          // Call the function from the Vue instance if it exists
          if (typeof el._trackClickInstance[fnName] === 'function') {
            trackValue = el._trackClickInstance[fnName]();
          }
        } catch (e) {
          console.error('Error evaluating track-click expression:', e);
        }
      }

      // Log trackValue in development mode
      if (process.env.NODE_ENV === 'development') {
        console.log('[TrackClick]', trackValue);
      }

      // Push to dataLayer if trackValue exists
      if (window && trackValue !== undefined) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'track_click',
          track_click_name: trackValue,
        });
      }
    });
  },

  // Update stored value when directive is updated
  update(el: HTMLElement, binding: any, vnode: VNode) {
    el._trackClickExpression = binding.expression;
    el._trackClickValue = binding.value;
    el._trackClickInstance = vnode.context;
  },
};
